import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { toast } from 'react-hot-toast'
import { ArrowRight, Clock, ShieldCheck, Trash2, XCircle } from 'lucide-react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Textarea } from '@goodday-clone/ui'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { useAuth } from '@/context/AuthContext'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import {
  useDecideDeletionRequest,
  useDeletionRequests,
  type CrmDeletionRequest,
} from '@/shared/hooks/useCrmDeletion'

// The delete queue for leads, shown where the leads are worked on. Sales can
// raise a request against any lead they own; only the superadmin sees this
// section and only their approval actually removes the row.

function summaryBadges(row: CrmDeletionRequest) {
  return Object.entries(row.entitySummary || {})
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .slice(0, 4)
    .map(([k, v]) => [k, String(v)] as const)
}

interface Props {
  onOpenLead: (leadId: string) => void
}

export function DeletionApprovalsSection({ onOpenLead }: Props) {
  const { user } = useAuth()
  const mayDecide = isSuperAdminOrOwner(user?.role)
  const { data: requests = [], isLoading } = useDeletionRequests({ status: 'all', entityType: 'lead', enabled: mayDecide })
  const decide = useDecideDeletionRequest()

  const [approveTarget, setApproveTarget] = useState<CrmDeletionRequest | null>(null)
  const [rejectTarget, setRejectTarget] = useState<CrmDeletionRequest | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const { pending, decided } = useMemo(() => {
    const open = requests.filter((r) => r.status === 'pending')
    const history = requests
      .filter((r) => r.status !== 'pending')
      .sort((a, b) => String(b.decidedAt || b.createdAt).localeCompare(String(a.decidedAt || a.createdAt)))
    return { pending: open, decided: history.slice(0, 3) }
  }, [requests])

  if (!mayDecide) return null

  const confirmApprove = async () => {
    if (!approveTarget) return
    setBusy(true)
    try {
      const res = await decide.mutateAsync({ id: approveTarget.id, action: 'approve' })
      toast.success(res?.message || 'Lead deleted.')
      setApproveTarget(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'approve this delete request'))
    } finally {
      setBusy(false)
    }
  }

  const confirmReject = async () => {
    if (!rejectTarget) return
    setBusy(true)
    try {
      await decide.mutateAsync({ id: rejectTarget.id, action: 'reject', note: note.trim() || undefined })
      toast.success('Delete request rejected. The lead stays.')
      setRejectTarget(null)
      setNote('')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'reject this delete request'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="border-amber-500/30">
      <CardHeader className="flex flex-row items-start justify-between gap-3 border-b border-border/50 py-4">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
            Deletion Approvals
            {pending.length > 0 && (
              <Badge className="gap-1 border border-amber-500/40 bg-amber-500/10 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                <Clock className="h-3 w-3" />
                {pending.length} awaiting
              </Badge>
            )}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Delete requests your team raised on leads. Approving removes the lead from the database for good.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild className="shrink-0 text-xs">
          <Link to="/app/sales/deletion-approvals">
            All approvals <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Link>
        </Button>
      </CardHeader>

      <CardContent className="space-y-2 p-4">
        {isLoading ? (
          <p className="py-4 text-center text-xs text-muted-foreground">Checking the delete queue…</p>
        ) : pending.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 py-5 text-center">
            <ShieldCheck className="h-7 w-7 text-emerald-500/70" />
            <p className="text-sm font-medium text-foreground">No lead is waiting to be deleted</p>
            {decided.length > 0 && (
              <div className="mt-2 w-full space-y-1.5 text-left">
                {decided.map((row) => (
                  <p key={row.id} className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{row.entityName}</span> ·{' '}
                    {row.requestedByName || 'someone'} ·{' '}
                    {row.decidedByName ? `${row.decidedByName} ${row.status}` : row.status} ·{' '}
                    {formatDistanceToNow(new Date(row.decidedAt || row.createdAt), { addSuffix: true })}
                  </p>
                ))}
              </div>
            )}
          </div>
        ) : (
          pending.map((row) => {
            const badges = summaryBadges(row)
            return (
            <div
              key={row.id}
              className="rounded-xl border border-amber-500/25 bg-amber-500/[0.03] px-4 py-3"
              data-testid="lead-deletion-request"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => onOpenLead(row.entityId)}
                    className="truncate text-sm font-semibold text-foreground hover:text-primary"
                  >
                    {row.entityName}
                  </button>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Requested by {row.requestedByName || 'a team member'} ·{' '}
                    {formatDistanceToNow(new Date(row.createdAt), { addSuffix: true })}
                  </p>
                  <p className="mt-1.5 text-xs text-foreground">
                    <span className="font-semibold uppercase tracking-wide text-muted-foreground">Reason: </span>
                    {row.reason}
                  </p>
                  {badges.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {badges.map(([k, v]) => (
                        <Badge key={k} variant="outline" className="text-[11px] font-normal">
                          {k}: {v}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button size="sm" variant="destructive" onClick={() => setApproveTarget(row)}>
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Approve delete
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setNote('')
                      setRejectTarget(row)
                    }}
                  >
                    <XCircle className="mr-1.5 h-3.5 w-3.5" /> Reject
                  </Button>
                </div>
              </div>
            </div>
            )
          })
        )}
      </CardContent>

      <ConfirmDialog
        open={!!approveTarget}
        onCancel={() => setApproveTarget(null)}
        onConfirm={confirmApprove}
        loading={busy}
        danger
        confirmLabel="Yes, delete it"
        title={`Delete "${approveTarget?.entityName}"?`}
        message={`This is final: the lead and everything recorded on it leave the database, and ${approveTarget?.requestedByName || 'the requester'} is told it happened.`}
      />

      <ConfirmDialog
        open={!!rejectTarget}
        onCancel={() => setRejectTarget(null)}
        onConfirm={confirmReject}
        loading={busy}
        confirmLabel="Keep the lead"
        cancelLabel="Cancel"
        title={`Reject the delete request for "${rejectTarget?.entityName}"?`}
        message="The lead stays untouched and the requester is told why."
      >
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Note for the requester</label>
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Keep it — this account is still being worked."
            className="text-sm"
          />
        </div>
      </ConfirmDialog>
    </Card>
  )
}
